import {
  ProductFilter,
  ProductFilterOption,
} from "@/lib/redux/productFilters/productFiltersSlice";
import { productFilterOptionSelectors } from "@/lib/redux/store";
import { forwardRef, PropsWithChildren, useMemo } from "react";
import GroupComponent from "./Group";
import ItemComponent from "./Item";
import { Container } from "./styles";

// Cast to fix React 17 + @types/react 18 compatibility issue
const Group = GroupComponent as any;
const Item = ItemComponent as any;

export type GroupValuesProps = {
  title: string;
  hideTitle: boolean | null;
};

type Props = PropsWithChildren<{
  filter: ProductFilter;
  groupValues?: GroupValuesProps[];
}>;

const Menu = forwardRef<HTMLDivElement, Props>(function Menu(props, ref) {
  const { filter, groupValues } = props;

  const allOptions = useMemo(() => {
    if (filter) {
      return productFilterOptionSelectors.selectAll(filter);
    }
  }, [filter]);

  const hiddenGroupTitles = useMemo(() => new Set(), []);

  const optionMap = useMemo(() => {
    const map = new Map<string | null, ProductFilterOption[]>();
    if (allOptions) {
      allOptions.forEach((option) => {
        // Create groups by splitting the value on " > "
        // and using the first string as group name.
        const [a, b] = option.value.split(" > ");
        // When value doesn't contain " > ", use null
        // as key to flag that the option doesn't belong
        // to any group.
        const key = a && b ? a : null;
        const value = map.get(key) || [];
        map.set(key, [...value, option]);
      });
    }

    const sortedMap = new Map<string | null, ProductFilterOption[]>();
    groupValues?.forEach(({ title, hideTitle }) => {
      if (hideTitle) hiddenGroupTitles.add(title);
      sortedMap.set(title, []);
    });
    return new Map([...sortedMap, ...map]);
  }, [allOptions, groupValues, hiddenGroupTitles]);

  const ungroupedOptions = optionMap.get(null);

  return (
    <Container ref={ref}>
      {Array.from(optionMap.entries()).map((entry) => {
        const [key, options] = entry;
        const hide = hiddenGroupTitles.has(key);
        if (key) {
          return (
            <Group key={key} label={key} hide={hide}>
              {options?.map((option) => {
                return (
                  <Item
                    key={option.value}
                    filterId={filter.id}
                    option={option}
                  />
                );
              })}
            </Group>
          );
        } else {
          return null;
        }
      })}
      {ungroupedOptions?.map((option) => (
        <Item key={option.value} filterId={filter.id} option={option} />
      ))}
    </Container>
  );
});

export default Menu;
